/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Global style types
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.12.0
 */

import { EnhancedStore } from '@reduxjs/toolkit';
import { FormikHelpers } from 'formik';
import { Reducer } from 'redux';
import { CSSProperties } from 'styled-components';

export type ValueOf<T> = T[keyof T];

export interface IAttrStyled {
    className?: any;
    dataName?: string;
    disabled?: boolean;
    style?: Partial<CSSProperties>;
}

export type ArrayElement<A> = A extends ReadonlyArray<infer T> ? T : never;

export type TConflicts = Array<{
    code: string;
    message: string;
}>;

export type TAvailableActions<T extends string> = {
    [key in T]?: {
        isDisabled?: boolean;
        isVisible: boolean;
        reason?: string;
        conflicts?: TConflicts;
    };
};

export interface IAsyncStore {
    asyncReducers: { [reducerName: string]: Reducer<any, any> };
    injectReducer: (key: string, asyncReducer: Reducer<any, any>) => void;
}

export type TStore = EnhancedStore & Partial<IAsyncStore>;

export type TOverflow = 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto';
export type TTextAlign = 'center' | 'justify' | 'left' | 'right' | 'start' | 'end';
export type TButtonKind = 'medium' | 'mediumOutline' | 'mediumNormal' | 'small' | 'smallOutline' | 'table' | 'circle';
export type TTooltipPlacement =
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';

interface IApiValidationError {
    code: string;
    field: string;
}
export interface IApiError {
    code: string | null;
    message: string | null;
    timestamp: string;
    validation: IApiValidationError[] | null;
}

export type TLinkTarget = '_self' | '_blank' | '_parent' | '_top';

export enum ERequestStatus {
    success = 'success',
    fail = 'fail',
}

export type FormSubmitHandler<T> = (data: T, formikHelpers: FormikHelpers<T>) => void;
